import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import { observer } from 'mobx-react-lite';

import { StaticMessageBubbles } from '@mcn-platform/converse-lib';
import { IPortal, useStore } from '@mcn-platform/models';

import { formatCachedSuggestedPromptAnswer } from '../../utils/formatCachedSuggestedPromptAnswer';

type PortalSuggestedPromptAnswerItem = {
  prompt: string;
  promptType: string;
  locale: string;
  answer?: string | null;
  cacheStatus: string;
  cachedAt?: string | null;
};

type PortalSuggestedPromptAnswersResult = {
  status: string;
  message?: string | null;
  locales?: string[] | null;
  items?: PortalSuggestedPromptAnswerItem[] | null;
};

type Props = {
  portal: IPortal;
};

function formatUnknownError(e: unknown): string {
  if (e == null) return 'Unknown error';
  if (typeof e === 'string') return e;
  if (e instanceof Error) return e.message || String(e);
  if (typeof e === 'object') {
    const o = e as Record<string, unknown>;
    if (Array.isArray(o.errors)) {
      const parts = o.errors.map((item: unknown) => {
        if (item && typeof item === 'object' && 'message' in item) {
          return String((item as { message?: string }).message ?? '');
        }
        return formatUnknownError(item);
      });
      const joined = parts.filter(Boolean).join('\n');
      if (joined) return joined;
    }
    if (typeof o.message === 'string') return o.message;
  }
  return String(e);
}

function rowKey(item: PortalSuggestedPromptAnswerItem): string {
  return `${item.locale}:${item.promptType}:${item.prompt}`;
}

const PortalSuggestedPromptsPanel = observer(({ portal }: Props) => {
  const store = useStore();
  const [data, setData] = useState<PortalSuggestedPromptAnswersResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [regeneratingKey, setRegeneratingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const organizationID = useMemo(
    () => String(
      portal.organization?.id
        || portal.organizationID
        || store.currentOrganization?.id
        || '',
    ),
    [portal, store.currentOrganization?.id],
  );
  const portalID = portal.id;

  const load = useCallback(async () => {
    if (!organizationID || !portalID) {
      setLoading(false);
      setError('Portal or organization not loaded.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await store.portalSuggestedPromptAnswers(
        organizationID,
        portalID,
      );
      setData(res as PortalSuggestedPromptAnswersResult);
      if (res?.status === 'ERROR') {
        setError(res.message || 'Failed to load suggested prompt answers');
      }
    } catch (e) {
      setError(formatUnknownError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [store, organizationID, portalID]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRegenerate = async (item: PortalSuggestedPromptAnswerItem) => {
    if (!organizationID || !portalID) return;
    const key = rowKey(item);
    setRegeneratingKey(key);
    setError(null);
    try {
      const res = await store.regeneratePortalSuggestedPromptAnswer(
        organizationID,
        portalID,
        item.promptType,
        item.prompt,
        item.locale,
      );
      if (res?.status === 'ERROR') {
        setError(res.message || 'Regeneration failed');
      }
      await load();
    } catch (e) {
      setError(formatUnknownError(e));
    } finally {
      setRegeneratingKey(null);
    }
  };

  const items = data?.items ?? [];
  const locales = useMemo(() => {
    if (data?.locales?.length) return data.locales;
    return [...new Set(items.map((item) => item.locale))].sort((a, b) => a.localeCompare(b));
  }, [data?.locales, items]);

  const renderSection = (
    title: string,
    subtitle: string,
    sectionItems: PortalSuggestedPromptAnswerItem[],
  ) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
      {sectionItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No prompts configured. Edit prompts under General → Display, then Save.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {sectionItems.map((item) => {
            const key = rowKey(item);
            const isRegenerating = regeneratingKey === key;
            return (
              <Paper key={key} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, minWidth: 200 }}>
                      {item.prompt}
                    </Typography>
                    <Chip
                      size="small"
                      label={item.cacheStatus === 'CACHED' ? 'Cached' : 'Not cached'}
                      color={item.cacheStatus === 'CACHED' ? 'success' : 'default'}
                      variant="outlined"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={isRegenerating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                      onClick={() => void handleRegenerate(item)}
                      disabled={isRegenerating || loading}
                    >
                      {isRegenerating ? 'Regenerating…' : 'Regenerate'}
                    </Button>
                  </Stack>
                  {item.cachedAt ? (
                    <Typography variant="caption" color="text.secondary">
                      Cached: {new Date(item.cachedAt).toLocaleString()}
                    </Typography>
                  ) : null}
                  <Divider />
                  {item.answer?.trim() ? (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <StaticMessageBubbles
                        hideQuestion
                        dense
                        prompt={item.prompt}
                        response={formatCachedSuggestedPromptAnswer(item.answer)}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No cached answer yet. Regenerate to create one.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 2, maxWidth: 960 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing cached answers for all configured locales.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void load()}
          disabled={loading || Boolean(regeneratingKey)}
        >
          Refresh
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          {locales.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No locales found for this portal.
            </Typography>
          ) : (
            locales.map((locale) => {
              const localeItems = items.filter((item) => item.locale === locale);
              const suggestedItems = localeItems.filter((item) => item.promptType === 'suggested');
              const persistentItems = localeItems.filter((item) => item.promptType === 'persistent');
              return (
                <Box key={locale} sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ textTransform: 'uppercase' }}>
                    {locale}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {renderSection(
                    'Welcome suggested prompts',
                    'Shown on the Shopping Assistant welcome screen when no product/category prompts are displayed.',
                    suggestedItems,
                  )}
                  {renderSection(
                    'Persistent prompts',
                    'Shown as secondary chips in the composer footer.',
                    persistentItems,
                  )}
                </Box>
              );
            })
          )}
        </>
      )}
    </Box>
  );
});

export default PortalSuggestedPromptsPanel;
